import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { PlusIcon, CheckIcon, XIcon, ChevronIcon, TrashIcon, EditIcon } from "../icons";
import { fmtMonth } from "../utils/helpers";
import S from "../styles";

// ─── Defaults ─────────────────────────────────────────
const DEFAULT_CATEGORIES = ["Filament", "Acrylic", "Hardware", "Machinery", "Parts", "Shipping Supplies", "Software", "Subscriptions", "Office Expenses", "Transportation", "Food"];
const DEFAULT_BRANDS = ["Hatchbox", "Polymaker", "eSUN", "Prusa", "Amazon Basics", "Uline", "McMaster-Carr", "Home Depot", "Other"];
const DEFAULT_COLORS = ["Black", "White", "Clear", "Red", "Blue", "Green", "Yellow", "Orange", "Natural", "Gray", "N/A"];
const DEFAULT_PURCHASERS = ["Daniel - Personal Card", "Jonathan - Personal Card", "Company Card", "Company Account", "Petty Cash"];

const CATEGORY_COLORS = {
  Filament: "#8b5cf6", Acrylic: "#3b82f6", Hardware: "#f59e0b", Machinery: "#ef4444",
  Parts: "#10b981", "Shipping Supplies": "#0033FF", Software: "#6366f1",
  Subscriptions: "#ec4899", "Office Expenses": "#14b8a6", Transportation: "#f97316", Food: "#84cc16",
};

// ─── Cell Dropdown ────────────────────────────────────
function CellDropdown({ value, options, onChange, onAddNew, label }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const dropRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  return (
    <div>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        style={{
          ...S.cellInput, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "space-between", background: "#F4F2EC", minHeight: 32, textAlign: "left",
          color: value ? "#0A0A0F" : "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value || `Select ${label}`}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(10,10,15,0.58)" strokeWidth="3" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={dropRef} style={{
          position: "fixed", top: pos.top, left: pos.left, width: Math.max(pos.width, 180),
          zIndex: 9999, background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
          maxHeight: 240, overflowY: "auto", boxShadow: "0 12px 32px rgba(0,0,0,.5)",
        }}>
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                padding: "8px 12px", fontSize: 14, cursor: "pointer", color: opt === value ? "#0033FF" : "#0A0A0F",
                background: opt === value ? "#F4F2EC" : "transparent", transition: "background .1s",
              }}
              onMouseEnter={(e) => { if (opt !== value) e.currentTarget.style.background = "rgba(22,45,80,0.5)"; }}
              onMouseLeave={(e) => { if (opt !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {opt}
            </div>
          ))}
          <div
            onClick={() => { setOpen(false); onAddNew(); }}
            style={{
              padding: "8px 12px", fontSize: 14, cursor: "pointer", color: "#0033FF",
              borderTop: "2px solid #0A0A0F", display: "flex", alignItems: "center", gap: 6,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22,45,80,0.5)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <PlusIcon /> Add new {label}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Add New Option Inline ────────────────────────────
function AddNewInline({ label, onSave, onCancel }) {
  const [val, setVal] = useState("");
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12 }}>
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={`New ${label}...`}
        style={{ ...S.cellInput, flex: 1, fontSize: 14 }}
        onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) onSave(val.trim()); if (e.key === "Escape") onCancel(); }}
      />
      <button onClick={() => val.trim() && onSave(val.trim())} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e" }}><CheckIcon /></button>
      <button onClick={onCancel} style={S.iconBtn}><XIcon /></button>
    </div>
  );
}

// ─── Reimbursement Card (for reports — shows totals only) ──
function ReimbursementCard({ expenses }) {
  const danielTotal = expenses
    .filter((e) => e.purchaser && e.purchaser.toLowerCase().startsWith("daniel"))
    .reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
  const jonathanTotal = expenses
    .filter((e) => e.purchaser && e.purchaser.toLowerCase().startsWith("jonathan"))
    .reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);

  return (
    <div style={{ background: "#FFFFFF", borderRadius: 0, padding: "14px 18px", border: "2px solid #0A0A0F", marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 10 }}>Reimbursements</div>
      <div style={{ display: "flex", gap: 20 }}>
        <div><span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>Daniel: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: danielTotal > 0 ? "#0033FF" : "rgba(10,10,15,0.58)" }}>${danielTotal.toFixed(2)}</span></div>
        <div><span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>Jonathan: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: jonathanTotal > 0 ? "#0033FF" : "rgba(10,10,15,0.58)" }}>${jonathanTotal.toFixed(2)}</span></div>
      </div>
    </div>
  );
}

// ─── Report View ──────────────────────────────────────
function ReportView({ expenses, reportType, reportPeriod }) {
  const filtered = useMemo(() => {
    if (reportType === "monthly") {
      return expenses.filter((e) => e.date && e.date.slice(0, 7) === reportPeriod);
    }
    if (reportType === "quarterly") {
      const [y, q] = reportPeriod.split("-Q");
      const qNum = parseInt(q);
      const startMonth = (qNum - 1) * 3 + 1;
      return expenses.filter((e) => {
        if (!e.date) return false;
        const m = parseInt(e.date.slice(5, 7));
        const yr = e.date.slice(0, 4);
        return yr === y && m >= startMonth && m < startMonth + 3;
      });
    }
    if (reportType === "annual") {
      return expenses.filter((e) => e.date && e.date.slice(0, 4) === reportPeriod);
    }
    return expenses;
  }, [expenses, reportType, reportPeriod]);

  const total = filtered.reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
  const byCat = {};
  filtered.forEach((e) => {
    const cat = e.category || "Uncategorized";
    byCat[cat] = (byCat[cat] || 0) + (parseFloat(e.cost) || 0);
  });
  const sorted = Object.entries(byCat).sort((a, b) => b[1] - a[1]);

  const labelMap = {
    monthly: reportPeriod ? fmtMonth(reportPeriod) : "",
    quarterly: reportPeriod ? reportPeriod.replace("-", " ") : "",
    annual: reportPeriod || "",
  };

  return (
    <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0A0A0F" }}>
          {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report — {labelMap[reportType]}
        </h3>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, color: "#0033FF" }}>
          ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </div>
      {sorted.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map(([cat, amt]) => {
            const pct = total > 0 ? (amt / total) * 100 : 0;
            return (
              <div key={cat}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 14, color: "#0A0A0F" }}>{cat}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0A0A0F" }}>
                    ${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pct.toFixed(0)}%)
                  </span>
                </div>
                <div style={{ height: 6, background: "#FFFFFF", borderRadius: 0, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: CATEGORY_COLORS[cat] || "rgba(10,10,15,0.58)", borderRadius: 0, transition: "width .3s" }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 20 }}>No expenses for this period.</div>
      )}
      <ReimbursementCard expenses={filtered} />
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// ─── Main Expenses Page ───────────────────────────────
export default function ExpensesPage({ expenses, setExpenses, storeTitle, store, hideReimbursements }) {
  // store prop is set for store-specific expenses (e.g. "designshop", "vibratovault")
  // When store is set, use /api/store-expenses endpoints; otherwise use /api/expenses
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [brands, setBrands] = useState(DEFAULT_BRANDS);
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [purchasers, setPurchasers] = useState(DEFAULT_PURCHASERS);
  const [collapsed, setCollapsed] = useState({});
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [addingMonth, setAddingMonth] = useState(null);
  const [newRow, setNewRow] = useState({ date: "", cost: "", quantity: "", category: "", description: "", brand: "", color: "", sku: "", purchaser: "" });
  const [addingNew, setAddingNew] = useState(null); // { field: "category" | "brand" | ... }
  const [filters, setFilters] = useState({ search: "", category: "", purchaser: "" });
  const [showReport, setShowReport] = useState(false);
  const [reportType, setReportType] = useState("monthly");
  const [reportPeriod, setReportPeriod] = useState("");

  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  // Group expenses by month
  const grouped = useMemo(() => {
    let list = [...expenses];
    // Apply filters
    if (filters.search) {
      const f = filters.search.toLowerCase();
      list = list.filter((e) =>
        (e.description || "").toLowerCase().includes(f) ||
        (e.brand || "").toLowerCase().includes(f) ||
        (e.sku || "").toLowerCase().includes(f) ||
        (e.category || "").toLowerCase().includes(f) ||
        (e.color || "").toLowerCase().includes(f) ||
        (e.purchaser || "").toLowerCase().includes(f)
      );
    }
    if (filters.category) list = list.filter((e) => e.category === filters.category);
    if (filters.purchaser) list = list.filter((e) => e.purchaser === filters.purchaser);

    const map = {};
    const sorted = [...list].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    for (const e of sorted) {
      const k = (e.date || "").slice(0, 7) || "undated";
      if (!map[k]) map[k] = [];
      map[k].push(e);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [expenses, filters]);

  const totalAll = expenses.reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);

  // Available periods for reports — always show full 12 months + 4 quarters per year
  const allYears = useMemo(() => {
    const set = new Set();
    expenses.forEach((e) => { if (e.date) set.add(e.date.slice(0, 4)); });
    set.add(String(new Date().getFullYear()));
    return [...set].sort().reverse();
  }, [expenses]);

  const allMonths = useMemo(() => {
    const months = [];
    for (const y of allYears) {
      for (let m = 12; m >= 1; m--) {
        months.push(`${y}-${String(m).padStart(2, "0")}`);
      }
    }
    return months.sort().reverse();
  }, [allYears]);

  const quarters = useMemo(() => {
    const qs = [];
    for (const y of allYears) {
      for (let q = 4; q >= 1; q--) {
        qs.push(`${y}-Q${q}`);
      }
    }
    return qs.sort().reverse();
  }, [allYears]);

  // Set default report period
  useEffect(() => {
    if (!reportPeriod) {
      const now = new Date();
      const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const curQ = `${now.getFullYear()}-Q${Math.ceil((now.getMonth() + 1) / 3)}`;
      const curYear = String(now.getFullYear());
      if (reportType === "monthly") setReportPeriod(curMonth);
      else if (reportType === "quarterly") setReportPeriod(curQ);
      else if (reportType === "annual") setReportPeriod(curYear);
    }
  }, [reportType, reportPeriod]);

  // CSV Import
  const fileInputRef = useRef(null);
  const handleCsvImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return;

      // Parse header
      const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
      const colMap = {};
      const aliases = {
        date: ["date"],
        cost: ["cost", "amount", "price", "total"],
        category: ["category", "type"],
        description: ["description", "desc", "item", "name", "memo"],
        brand: ["brand", "vendor", "supplier"],
        color: ["color", "colour"],
        quantity: ["quantity", "qty", "count", "units"],
        sku: ["sku", "item number", "item no", "part number"],
        purchaser: ["purchased by", "purchaser", "payment method", "payment", "paid by", "who purchased"],
      };
      header.forEach((h, i) => {
        for (const [field, keys] of Object.entries(aliases)) {
          if (keys.some((k) => h.includes(k))) { colMap[field] = i; break; }
        }
      });

      const imported = [];
      for (let i = 1; i < lines.length; i++) {
        // Handle quoted CSV values
        const vals = [];
        let cur = "";
        let inQuote = false;
        for (const ch of lines[i]) {
          if (ch === '"') { inQuote = !inQuote; continue; }
          if (ch === "," && !inQuote) { vals.push(cur.trim()); cur = ""; continue; }
          cur += ch;
        }
        vals.push(cur.trim());

        const get = (field) => (colMap[field] !== undefined ? vals[colMap[field]] || "" : "");

        // Normalize date
        let date = get("date");
        if (date && !date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const d = new Date(date);
          if (!isNaN(d)) date = d.toISOString().slice(0, 10);
          else date = "";
        }

        const cost = get("cost").replace(/[$,]/g, "");
        if (!date && !cost) continue;

        imported.push({
          id: Date.now() + i,
          date,
          cost,
          quantity: get("quantity"),
          category: get("category"),
          description: get("description"),
          brand: get("brand"),
          color: get("color"),
          sku: get("sku"),
          purchaser: get("purchaser"),
        });
      }

      if (imported.length > 0) {
        const url = store ? `${API_URL}/api/store-expenses/batch` : `${API_URL}/api/expenses/batch`;
        const body = store ? { store, expenses: imported } : imported;
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((created) => setExpenses([...expenses, ...created]))
          .catch(() => setExpenses([...expenses, ...imported]));
        // Add any new categories/brands/etc from import
        imported.forEach((row) => {
          if (row.category && !categories.includes(row.category)) setCategories((c) => [...c, row.category]);
          if (row.brand && !brands.includes(row.brand)) setBrands((b) => [...b, row.brand]);
          if (row.color && !colors.includes(row.color)) setColors((c) => [...c, row.color]);
          if (row.purchaser && !purchasers.includes(row.purchaser)) setPurchasers((p) => [...p, row.purchaser]);
        });
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleAddNew = (field) => setAddingNew({ field });

  const saveNewOption = (field, val) => {
    if (field === "category" && !categories.includes(val)) setCategories([...categories, val]);
    if (field === "brand" && !brands.includes(val)) setBrands([...brands, val]);
    if (field === "color" && !colors.includes(val)) setColors([...colors, val]);
    if (field === "purchaser" && !purchasers.includes(val)) setPurchasers([...purchasers, val]);
    setAddingNew(null);
  };

  const startAdd = (monthKey) => {
    const today = new Date().toISOString().slice(0, 10);
    const defaultDate = monthKey && monthKey !== "undated" ? `${monthKey}-01` : today;
    setAddingMonth(monthKey || today.slice(0, 7));
    setNewRow({ date: defaultDate, cost: "", quantity: "", category: "", description: "", brand: "", color: "", sku: "", purchaser: "" });
    setCollapsed((c) => ({ ...c, [monthKey]: false }));
  };

  const addRow = () => {
    if (!newRow.date || !newRow.cost) return;
    const url = store ? `${API_URL}/api/store-expenses` : `${API_URL}/api/expenses`;
    const body = store ? { ...newRow, store } : newRow;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((created) => setExpenses([...expenses, created]))
      .catch(() => {
        const id = Date.now();
        setExpenses([...expenses, { ...newRow, id, cost: newRow.cost }]);
      });
    setAddingMonth(null);
    setNewRow({ date: "", cost: "", quantity: "", category: "", description: "", brand: "", color: "", sku: "", purchaser: "" });
  };

  const saveEdit = () => {
    const url = store
      ? `${API_URL}/api/store-expenses/${store}/${editId}`
      : `${API_URL}/api/expenses/${editId}`;
    fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editRow),
    })
      .then((r) => r.json())
      .then((updated) => setExpenses(expenses.map((e) => (e.id === editId ? updated : e))))
      .catch(() => setExpenses(expenses.map((e) => (e.id === editId ? { ...editRow } : e))));
    setEditId(null);
  };

  const deleteRow = (id) => {
    const url = store
      ? `${API_URL}/api/store-expenses/${store}/${id}`
      : `${API_URL}/api/expenses/${id}`;
    fetch(url, { method: "DELETE" })
      .then(() => setExpenses(expenses.filter((e) => e.id !== id)))
      .catch(() => setExpenses(expenses.filter((e) => e.id !== id)));
  };

  const markPaid = (person, ids) => {
    setExpenses(expenses.map((e) => ids.includes(e.id) ? { ...e, reimbursed: true } : e));
    ids.forEach((id) => {
      const url = store
        ? `${API_URL}/api/store-expenses/${store}/${id}`
        : `${API_URL}/api/expenses/${id}`;
      fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reimbursed: true }),
      }).catch(() => {});
    });
  };

  const fmtCost = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? "$0.00" : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const renderRow = (e, isNew) => {
    const row = isNew ? newRow : (editId === e.id ? editRow : e);
    const isEditing = isNew || editId === e.id;
    const setRow = isNew ? setNewRow : setEditRow;
    const set = (k, v) => setRow({ ...row, [k]: v });

    if (isEditing) {
      const handleKeyDown = (ev) => { if (ev.key === "Enter") { ev.preventDefault(); isNew ? addRow() : saveEdit(); } };
      return (
        <tr key={isNew ? "new" : e.id} style={S.addingRow} onKeyDown={handleKeyDown}>
          <td style={S.td}><input type="date" value={row.date} onChange={(ev) => set("date", ev.target.value)} style={{ ...S.cellInput, fontSize: 13, colorScheme: "dark" }} /></td>
          <td style={S.td}><input type="number" value={row.cost} onChange={(ev) => set("cost", ev.target.value)} placeholder="0.00" step="0.01" style={{ ...S.cellInput, fontSize: 13, textAlign: "right" }} /></td>
          <td style={S.td}><input type="number" value={row.quantity || ""} onChange={(ev) => set("quantity", ev.target.value)} placeholder="1" min="1" style={{ ...S.cellInput, fontSize: 13, textAlign: "center", width: 60 }} /></td>
          <td style={S.td}><CellDropdown value={row.category} options={categories} onChange={(v) => set("category", v)} onAddNew={() => handleAddNew("category")} label="category" /></td>
          <td style={S.td}><input value={row.description} onChange={(ev) => set("description", ev.target.value)} placeholder="Description" style={{ ...S.cellInput, fontSize: 13 }} /></td>
          <td style={S.td}><CellDropdown value={row.brand} options={brands} onChange={(v) => set("brand", v)} onAddNew={() => handleAddNew("brand")} label="brand" /></td>
          <td style={S.td}><CellDropdown value={row.color} options={colors} onChange={(v) => set("color", v)} onAddNew={() => handleAddNew("color")} label="color" /></td>
          <td style={S.td}><input value={row.sku} onChange={(ev) => set("sku", ev.target.value)} placeholder="SKU" style={{ ...S.cellInput, fontSize: 13 }} /></td>
          <td style={S.td}><CellDropdown value={row.purchaser} options={purchasers} onChange={(v) => set("purchaser", v)} onAddNew={() => handleAddNew("payment method")} label="payment method" /></td>
          <td style={{ ...S.td, textAlign: "right" }}>
            <button onClick={isNew ? addRow : saveEdit} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e" }}><CheckIcon /></button>
            <button onClick={() => isNew ? setAddingMonth(null) : setEditId(null)} style={{ ...S.iconBtn, marginLeft: 4 }}><XIcon /></button>
          </td>
        </tr>
      );
    }

    return (
      <tr key={e.id}>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap" }}>
          {e.date ? new Date(e.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
        </td>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: "#0033FF", fontWeight: 600 }}>{fmtCost(e.cost)}</td>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "center", color: "#0A0A0F" }}>{e.quantity || 1}</td>
        <td style={S.td}>
          {e.category ? (
            <span style={{
              display: "inline-block", padding: "2px 8px", borderRadius: 0, fontSize: 13, fontWeight: 600,
              background: `${CATEGORY_COLORS[e.category] || "rgba(10,10,15,0.58)"}20`, color: CATEGORY_COLORS[e.category] || "rgba(10,10,15,0.58)",
              border: `1px solid ${CATEGORY_COLORS[e.category] || "rgba(10,10,15,0.58)"}30`,
            }}>{e.category}</span>
          ) : "—"}
        </td>
        <td style={{ ...S.td, color: "#0A0A0F", fontSize: 14, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.description || "—"}</td>
        <td style={{ ...S.td, color: "#0A0A0F", fontSize: 14 }}>{e.brand || "—"}</td>
        <td style={{ ...S.td, color: "#0A0A0F", fontSize: 14 }}>{e.color || "—"}</td>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{e.sku || "—"}</td>
        <td style={{ ...S.td, fontSize: 13, color: e.purchaser && e.purchaser.toLowerCase().includes("personal") ? "#0033FF" : "#0A0A0F" }}>{e.purchaser || "—"}</td>
        <td style={{ ...S.td, textAlign: "right" }}>
          <button onClick={() => { setEditId(e.id); setEditRow({ ...e }); }} style={S.iconBtn} title="Edit"><EditIcon /></button>
          <button onClick={() => deleteRow(e.id)} style={{ ...S.iconBtn, marginLeft: 4 }} title="Delete"><TrashIcon /></button>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeTitle ? `${storeTitle} — Expenses` : "Expenses"}</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track purchases, reimbursements, and spending reports</p>
      </div>

      {/* Add new option inline */}
      {addingNew && (
        <AddNewInline
          label={addingNew.field}
          onSave={(val) => saveNewOption(addingNew.field, val)}
          onCancel={() => setAddingNew(null)}
        />
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Expenses</span><span style={S.statValue}>{fmtCost(totalAll)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>This Month</span><span style={S.statValue}>{fmtCost(expenses.filter((e) => e.date && e.date.slice(0, 7) === new Date().toISOString().slice(0, 7)).reduce((s, e) => s + (parseFloat(e.cost) || 0), 0))}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Entries</span><span style={S.statValue}>{expenses.length}</span></div>
      </div>

      {/* Filters + Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search expenses..."
            style={{ ...S.input, marginBottom: 0, maxWidth: 220, fontSize: 14 }}
          />
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            style={{ ...S.input, marginBottom: 0, maxWidth: 160, fontSize: 14, cursor: "pointer" }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={filters.purchaser}
            onChange={(e) => setFilters({ ...filters, purchaser: e.target.value })}
            style={{ ...S.input, marginBottom: 0, maxWidth: 200, fontSize: 14, cursor: "pointer" }}
          >
            <option value="">All Payment Methods</option>
            {purchasers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {(filters.search || filters.category || filters.purchaser) && (
            <button onClick={() => setFilters({ search: "", category: "", purchaser: "" })} style={{ ...S.clearBtn, fontSize: 13, padding: "8px 12px" }}>Clear</button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowReport(!showReport)} style={{ ...S.addBtn, fontSize: 13 }}>
            {showReport ? "Hide Report" : "Reports"}
          </button>
          <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={{ ...S.addBtn, fontSize: 13 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvImport} style={{ display: "none" }} />
          <button onClick={() => startAdd(new Date().toISOString().slice(0, 7))} style={S.addBtn}>
            <PlusIcon /> Add Expense
          </button>
        </div>
      </div>

      {/* Report Panel */}
      {showReport && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4, background: "#FFFFFF", borderRadius: 0, padding: 3, border: "2px solid #0A0A0F" }}>
              {["monthly", "quarterly", "annual"].map((t) => (
                <button
                  key={t}
                  onClick={() => { setReportType(t); setReportPeriod(""); }}
                  style={{ ...S.navBtn, fontSize: 13, padding: "6px 12px", ...(reportType === t ? S.navBtnActive : {}) }}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            <select
              value={reportPeriod}
              onChange={(e) => setReportPeriod(e.target.value)}
              style={{ ...S.input, marginBottom: 0, maxWidth: 180, fontSize: 14, cursor: "pointer" }}
            >
              {reportType === "monthly" && allMonths.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
              {reportType === "quarterly" && quarters.map((q) => <option key={q} value={q}>{q.replace("-", " ")}</option>)}
              {reportType === "annual" && allYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {reportPeriod && <ReportView expenses={expenses} reportType={reportType} reportPeriod={reportPeriod} />}
        </div>
      )}

      {/* Monthly groups */}
      {addingMonth && !grouped.some(([k]) => k === addingMonth) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...S.monthHeader, borderColor: "#0A0A0F", marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>
                {addingMonth === "undated" ? "Undated" : fmtMonth(addingMonth)}
              </span>
            </div>
          </div>
          <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0, overflowX: "auto" }}>
            <table style={{ ...S.table, minWidth: 1000 }}>
              <thead><tr>{renderHeaders()}</tr></thead>
              <tbody>{renderRow(null, true)}</tbody>
            </table>
          </div>
        </div>
      )}

      {grouped.map(([mk, me]) => {
        const isOpen = collapsed[mk] !== true;
        const monthTotal = me.reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);

        // Reimbursement totals for this month
        const unpaidDaniel = !hideReimbursements ? me.filter((e) => e.purchaser && e.purchaser.toLowerCase().startsWith("daniel") && !e.reimbursed) : [];
        const unpaidJonathan = !hideReimbursements ? me.filter((e) => e.purchaser && e.purchaser.toLowerCase().startsWith("jonathan") && !e.reimbursed) : [];
        const danielOwed = unpaidDaniel.reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
        const jonathanOwed = unpaidJonathan.reduce((s, e) => s + (parseFloat(e.cost) || 0), 0);
        const hasReimbursements = danielOwed > 0 || jonathanOwed > 0;

        return (
          <div key={mk} style={{ marginBottom: 16 }}>
            <button onClick={() => toggle(mk)} style={{ ...S.monthHeader, borderColor: "#0A0A0F" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>
                  {mk === "undated" ? "Undated" : fmtMonth(mk)}
                </span>
                <span style={S.jobCountBadge}>{me.length} item{me.length !== 1 ? "s" : ""}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Total</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#0033FF" }}>{fmtCost(monthTotal)}</div>
                </div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); startAdd(mk); }}
                  style={{ ...S.addBtn, padding: "4px 10px", fontSize: 13 }}
                >
                  <PlusIcon /> Add
                </button>
              </div>
            </button>
            {isOpen && (
              <>
                {/* Reimbursements owed for this month */}
                {hasReimbursements && (
                  <div style={{ background: "#FFFFFF", borderLeft: "2px solid #0A0A0F", borderRight: "2px solid #0A0A0F", padding: "12px 18px", display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)" }}>Reimbursements Owed</span>
                    {danielOwed > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>Daniel:</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: "#0033FF" }}>{fmtCost(danielOwed)}</span>
                        <button
                          onClick={(ev) => { ev.stopPropagation(); markPaid("daniel", unpaidDaniel.map((e) => e.id)); }}
                          style={{
                            padding: "3px 10px", borderRadius: 0, border: "1px solid #22c55e", background: "transparent",
                            color: "#22c55e", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                          }}
                        >
                          Mark Paid
                        </button>
                      </div>
                    )}
                    {jonathanOwed > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>Jonathan:</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: "#0033FF" }}>{fmtCost(jonathanOwed)}</span>
                        <button
                          onClick={(ev) => { ev.stopPropagation(); markPaid("jonathan", unpaidJonathan.map((e) => e.id)); }}
                          style={{
                            padding: "3px 10px", borderRadius: 0, border: "1px solid #22c55e", background: "transparent",
                            color: "#22c55e", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
                          }}
                        >
                          Mark Paid
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0, overflowX: "auto" }}>
                  <table style={{ ...S.table, minWidth: 1000 }}>
                    <thead><tr>{renderHeaders()}</tr></thead>
                    <tbody>
                      {addingMonth === mk && renderRow(null, true)}
                      {me.map((e) => renderRow(e, false))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      })}

      {grouped.length === 0 && !addingMonth && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 60, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
          No expenses recorded yet. Click "Add Expense" to get started.
        </div>
      )}
    </div>
  );
}

function renderHeaders() {
  return (
    <>
      <th style={{ ...S.th, width: "8%" }}>Date</th>
      <th style={{ ...S.th, width: "8%", textAlign: "right" }}>Cost</th>
      <th style={{ ...S.th, width: "5%", textAlign: "center" }}>Qty</th>
      <th style={{ ...S.th, width: "11%" }}>Category</th>
      <th style={{ ...S.th, width: "16%" }}>Description</th>
      <th style={{ ...S.th, width: "9%" }}>Brand</th>
      <th style={{ ...S.th, width: "7%" }}>Color</th>
      <th style={{ ...S.th, width: "9%" }}>SKU</th>
      <th style={{ ...S.th, width: "13%" }}>Purchased By</th>
      <th style={{ ...S.th, width: "9%", textAlign: "right" }}>Actions</th>
    </>
  );
}
